import { CommitteeModel, CommitteeModelExtended } from "@api/models/committees/CommitteeModel";
import { CommitteeModelInterface } from "@api/models/interfaces/committees/CommitteeModelInterface";
import { UserProfile } from "@api/models/UserProfile";
import { formatIntervalDate, getDateByTimeZoneOffset } from "@common/dateTimeHelper";
import i18n from "@service/locale/i18";
import { CommitteeViewModel, CommitteeViewModelInterface } from "@share/committees/committee/types/CommitteeViewModel";
import { getUserInfoForSelectTitle } from "@share/common/helpers/userProfileNameHelper";
import { TicketType } from "@share/ticketSystem/enums/TicketTypes";
import { CommitteeFormModel, CommitteeFormModelInterface } from "../types/CommitteeFormModel";
import { getFullCompanyName } from '@common/CompanyHelper';

/**
 * This mapper need for mapping committee data
 * from backendEntity to form data and back
 */
export class CommitteeModelMapper {
    static toForm(committee: CommitteeModelExtended, profiles: UserProfile[]): CommitteeFormModelInterface {

        const secriteryProfile = profiles.find(profile => profile.userId === committee.secretary);

        const data: CommitteeFormModelInterface = {
            id: committee.id,
            title: committee.title,
            description: committee.description,
            userProfiles: profiles.filter(profile => committee.userIds.includes(profile.userId || 0)),
            ticketIds: committee.ticketIds,
            beginAt: committee.beginAt,
            endAt: committee.endAt,
            subjectType: { id: committee.subjectType, title: i18n.t(`tickets.columns.${committee.subjectType}`).toString() },
            company: { id: committee.company.id, title: getFullCompanyName(committee.company) },
            secretary: { id: committee.secretary, title: secriteryProfile && getUserInfoForSelectTitle(secriteryProfile, "direction") || "" },
            location: committee.location,
            link: committee.link
        }

        return new CommitteeFormModel(data)
    }

    static fromForm(formModel: CommitteeFormModelInterface): CommitteeModel {
        if (!formModel.beginAt || !formModel.endAt) {
            throw new Error("The dates didn't setted.");
        }

        const data: CommitteeModelInterface = {
            id: formModel.id,
            title: formModel.title,
            description: formModel.description,
            subjectType: formModel.subjectType.id as TicketType,
            company: formModel.company.id,
            secretary: formModel.secretary.id,
            // DATES:
            beginAt: formModel.beginAt,
            endAt: formModel.endAt,

            location: formModel.location,
            link: formModel.link,

            userIds: formModel.userProfiles.map(item => item.userId || 0),
            ticketIds: formModel.ticketIds
        }

        return new CommitteeModel(data);
    }

    static toView(committee: CommitteeModelExtended, profiles: UserProfile[], selectedTimeZoneOffset: number | null, lang = "en"): CommitteeViewModelInterface {

        const secretaryProfile = profiles.find((profile) => profile.userId === committee.secretary);

        const data: CommitteeViewModelInterface = {
            id: committee.id,
            title: committee.title,
            secretary: secretaryProfile && getUserInfoForSelectTitle(secretaryProfile, "direction") || "",
            ticketIds: committee.ticketIds,
            membersUserProfiles: profiles.filter((profile) => committee.userIds.includes(profile.userId || 0)),
            description: committee.description,

            link: committee.link,
            location: committee.location,

            company: getFullCompanyName(committee.company),

            subjectTypeText: committee.subjectType === TicketType.idea ? i18n.t('common.ticketSystemIdeas').toString() : i18n.t('common.ticketSystemProblems').toString(),
            subjectType: committee.subjectType,
            date: formatIntervalDate(getDateByTimeZoneOffset(committee.beginAt, selectedTimeZoneOffset).toObject(), getDateByTimeZoneOffset(committee.endAt, selectedTimeZoneOffset).toObject(), lang, false, true),
            isClosed: committee.isClosed
        }

        return new CommitteeViewModel(data)
    }
}
